import api from './api';
import type { PlanoTrabalho } from '../types/planoTrabalho.types';

export interface PlanoWorkflowPayload {
  motivo?: string;
  parecerTecnico?: string;
  parecerJuridico?: string;
  etapaDestino?: string;
  etapaRetorno?: string;
}

export interface PlanoTrabalhoAnaliseJuridicaFase {
  id?: string;
  faseId: string;
  observacaoFase?: string;
  ordem?: number;
}

export interface PlanoTrabalhoAnaliseJuridicaItem {
  id?: string;
  faseId: string;
  itemCodigo: string;
  statusResposta: 'CONFORME' | 'NAO_CONFORME' | 'PENDENTE' | 'NAO_APLICAVEL';
  observacaoItem?: string;
  ordem?: number;
}

export interface PlanoTrabalhoAnaliseJuridica {
  id?: string;
  planoTrabalhoId?: string;
  checklistVersao: string;
  encaminhamentoFinal?: 'APROVACAO' | 'APROVACAO_COM_RESSALVAS' | 'REJEICAO' | 'DILIGENCIA';
  fundamentacao?: string;
  parecerConsolidado?: string;
  numeroParecer?: string;
  anoParecer?: number;
  criadoPor?: string;
  atualizadoPor?: string;
  dataCriacao?: string;
  dataAtualizacao?: string;
  enviadoAprovacaoEm?: string | null;
  enviadoAprovacaoPor?: string | null;
  fases: PlanoTrabalhoAnaliseJuridicaFase[];
  itens: PlanoTrabalhoAnaliseJuridicaItem[];
}

export interface PlanoTrabalhoAnaliseJuridicaCatalogoItem {
  codigo: string;
  descricao: string;
  fundamentoLegal: string;
  ordem: number;
}

export interface PlanoTrabalhoAnaliseJuridicaCatalogoGrupo {
  id: string;
  label: string;
  ordem: number;
  itens: PlanoTrabalhoAnaliseJuridicaCatalogoItem[];
}

export interface PlanoTrabalhoAnaliseJuridicaCatalogoFase {
  id: string;
  titulo: string;
  descricao: string;
  ordem: number;
  grupos: PlanoTrabalhoAnaliseJuridicaCatalogoGrupo[];
}

export interface PlanoTrabalhoAnaliseJuridicaCatalogo {
  modalidade: string;
  versao: string;
  fases: PlanoTrabalhoAnaliseJuridicaCatalogoFase[];
}

export interface PlanoTrabalhoAnexo {
  id: string;
  planoTrabalhoId: string;
  etapa: string;
  tipoDocumento: string;
  nomeOriginal: string;
  nomeArquivo: string;
  mimeType?: string;
  tamanhoBytes?: number;
  descricao?: string;
  usuarioUpload?: string;
  uploaderNome?: string;
  dataUpload?: string;
  viewUrl?: string;
  documentoNumero?: string;
  documentCode?: string;
  origemRetificacao?: boolean;
}

export interface PlanoTrabalhoParecerAnexo {
  id: string;
  planoTrabalhoId: string;
  tipo?: string;
  nomeOriginal: string;
  mimeType: string;
  tamanhoBytes: number;
  usuarioUpload: string;
  dataUpload: string;
  documentCode?: string;
}

export interface PlanoTrabalhoHistorico {
  id: string;
  planoTrabalhoId: string;
  acao: string;
  etapa?: string;
  motivo?: string;
  decisao?: string;
  parecerTexto?: string;
  documentoTipo?: string;
  documentoNome?: string;
  usuario?: string;
  usuarioNome?: string;
  dataHora?: string;
}

export interface ComissaoMembroPainelPlano {
  planoTrabalhoId: string;
  titulo: string;
  emendaCodigo?: string | null;
  status?: string;
  etapaAtual?: string;
  numeroDaComissao?: number | null;
  statusPainel: string;
  parecerPendente: boolean;
  parecerRegistrado: boolean;
  ataAnexada: boolean;
  pareceresConcluidos: number;
  totalMembros: number;
  dataAtualizacao?: string | null;
}

export interface PlanoTrabalhoFilters {
  status?: string;
  modalidade?: string;
  etapaAtual?: string;
  minhaFila?: boolean;
}

const service = {
  listAll: async (filters?: PlanoTrabalhoFilters): Promise<PlanoTrabalho[]> => {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.modalidade) params.set('modalidade', filters.modalidade);
    if (filters?.etapaAtual) params.set('etapaAtual', filters.etapaAtual);
    if (filters?.minhaFila === false) params.set('minhaFila', 'false');
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await api.get(`/plano-trabalho${query}`);
    return res.data;
  },
  listarGestoresElegiveis: async (): Promise<Array<{ id: number; nomeCompleto: string; cargoFuncao?: string; secretaria?: string; documento?: string; email?: string }>> => {
    const res = await api.get('/plano-trabalho/gestores-elegiveis');
    return res.data;
  },
  listByInstituicao: async (instituicaoId: string, filters?: PlanoTrabalhoFilters): Promise<PlanoTrabalho[]> => {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.modalidade) params.set('modalidade', filters.modalidade);
    if (filters?.etapaAtual) params.set('etapaAtual', filters.etapaAtual);
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await api.get(`/plano-trabalho/instituicao/${instituicaoId}${query}`);
    return res.data;
  },
  get: async (id: string): Promise<PlanoTrabalho> => {
    const res = await api.get(`/plano-trabalho/${id}`);
    return res.data;
  },
  getByEmenda: async (emendaId: string): Promise<PlanoTrabalho | null> => {
    const res = await api.get(`/plano-trabalho/emenda/${emendaId}/atual`);
    if (res.status === 204) return null;
    return res.data;
  },
  getFull: async (id: string) => {
    const res = await api.get(`/plano-trabalho/full/${id}`);
    return res;
  },
  create: async (payload: Partial<PlanoTrabalho>) => {
    const res = await api.post('/plano-trabalho', payload);
    return res.data;
  },
  update: async (id: string, payload: Partial<PlanoTrabalho>) => {
    const res = await api.put(`/plano-trabalho/${id}`, payload);
    return res.data;
  },
  delete: async (id: string) => {
    await api.delete(`/plano-trabalho/${id}`);
  },
  aprovar: async (id: string, payload: string | PlanoWorkflowPayload) => {
    const body = typeof payload === 'string' ? { motivo: payload } : payload;
    const res = await api.post(`/plano-trabalho/${id}/aprovar`, body);
    return res.data;
  },
  reprovar: async (id: string, payload: string | PlanoWorkflowPayload) => {
    const body = typeof payload === 'string' ? { motivo: payload } : payload;
    const res = await api.post(`/plano-trabalho/${id}/reprovar`, body);
    return res.data;
  },
  devolver: async (id: string, payload: string | PlanoWorkflowPayload) => {
    const body = typeof payload === 'string' ? { motivo: payload } : payload;
    const res = await api.post(`/plano-trabalho/${id}/devolver`, body);
    return res.data;
  },
  solicitarRetificacao: async (id: string, motivo: string, etapaDestino?: string, etapaRetorno?: string) => {
    const res = await api.post(`/plano-trabalho/${id}/solicitar-retificacao`, { motivo, etapaDestino, etapaRetorno });
    return res.data;
  },
  enviarRetificacao: async (id: string) => {
    const res = await api.post(`/plano-trabalho/${id}/enviar-retificacao`);
    return res.data;
  },
  juntarDocumento: async (id: string, tipoDocumento: string, etapa: string, file: File, justificativa: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('tipoDocumento', tipoDocumento);
    formData.append('etapa', etapa);
    formData.append('justificativa', justificativa);
    const res = await api.post(`/plano-trabalho/${id}/juntada-documento`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data as PlanoTrabalhoAnexo;
  },
  confirmarReprovacao: async (id: string) => {
    const res = await api.post(`/plano-trabalho/${id}/confirmar-reprovacao`);
    return res.data;
  },
  enviar: async (id: string) => {
    const res = await api.post(`/plano-trabalho/${id}/enviar`);
    return res.data;
  },
  concluirParaAssinatura: async (id: string) => {
    const res = await api.post(`/plano-trabalho/${id}/concluir-para-assinatura`);
    return res.data;
  },
  getHistorico: async (id: string) => {
    const res = await api.get(`/plano-trabalho/${id}/historico`);
    return res.data as PlanoTrabalhoHistorico[];
  },
  getAnaliseJuridica: async (id: string) => {
    const res = await api.get(`/plano-trabalho/${id}/analise-juridica`);
    return res.data as PlanoTrabalhoAnaliseJuridica;
  },
  getAnaliseJuridicaCatalogo: async (modalidade = 'TERMO_FOMENTO') => {
    const res = await api.get(`/plano-trabalho/analise-juridica/catalogo?modalidade=${encodeURIComponent(modalidade)}`);
    return res.data as PlanoTrabalhoAnaliseJuridicaCatalogo;
  },
  saveAnaliseJuridica: async (id: string, payload: PlanoTrabalhoAnaliseJuridica) => {
    const res = await api.put(`/plano-trabalho/${id}/analise-juridica`, payload);
    return res.data as PlanoTrabalhoAnaliseJuridica;
  },
  enviarAnaliseParaAprovacao: async (id: string) => {
    const res = await api.post(`/plano-trabalho/${id}/analise-juridica/enviar-aprovacao`);
    return res.data as PlanoTrabalhoAnaliseJuridica;
  },
  salvarParecerTecnico: async (id: string, parecerTecnico: string): Promise<PlanoTrabalho> => {
    const res = await api.post(`/plano-trabalho/${id}/parecer-tecnico`, { parecerTecnico });
    return res.data as PlanoTrabalho;
  },
  listAnexos: async (id: string) => {
    const res = await api.get(`/plano-trabalho/${id}/anexos`);
    return res.data as PlanoTrabalhoAnexo[];
  },
  uploadAnexo: async (id: string, payload: { file: File; etapa: string; tipoDocumento: string; descricao?: string; documentoNumero?: string }) => {
    const formData = new FormData();
    formData.append('file', payload.file);
    formData.append('etapa', payload.etapa);
    formData.append('tipoDocumento', payload.tipoDocumento);
    if (payload.descricao) formData.append('descricao', payload.descricao);
    if (payload.documentoNumero) formData.append('documentoNumero', payload.documentoNumero);
    const res = await api.post(`/plano-trabalho/${id}/anexos/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data as PlanoTrabalhoAnexo;
  },
  deleteAnexo: async (id: string, anexoId: string) => {
    await api.delete(`/plano-trabalho/${id}/anexos/${anexoId}`);
  },
  editarFaseAdmin: async (id: string, payload: { novoStatus: string; novaEtapa: string | null; motivo?: string }): Promise<PlanoTrabalho> => {
    const res = await api.post(`/plano-trabalho/${id}/admin/editar-fase`, payload);
    return res.data as PlanoTrabalho;
  },
  getParecerAnexo: async (id: string, tipo?: string): Promise<PlanoTrabalhoParecerAnexo | null> => {
    try {
      const res = await api.get(`/plano-trabalho/${id}/parecer-anexo`, { params: tipo ? { tipo } : undefined });
      return res.data as PlanoTrabalhoParecerAnexo;
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  },
  uploadParecerAnexo: async (id: string, file: File, tipo?: string): Promise<PlanoTrabalhoParecerAnexo> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post(`/plano-trabalho/${id}/parecer-anexo`, formData, {
      headers: { 'Content-Type': undefined },
      params: tipo ? { tipo } : undefined,
    });
    return res.data as PlanoTrabalhoParecerAnexo;
  },
  deleteParecerAnexo: async (id: string, tipo?: string): Promise<void> => {
    await api.delete(`/plano-trabalho/${id}/parecer-anexo`, { params: tipo ? { tipo } : undefined });
  },
  parecerAnexoDownloadUrl: (id: string, tipo?: string): string => {
    const base = (api.defaults.baseURL ?? '/api').replace(/\/$/, '');
    const suffix = tipo ? `?tipo=${encodeURIComponent(tipo)}` : '';
    return `${base}/plano-trabalho/${id}/parecer-anexo/download${suffix}`;
  },
  downloadParecerAnexo: async (id: string, tipo?: string): Promise<Blob> => {
    const res = await api.get(`/plano-trabalho/${id}/parecer-anexo/download`, {
      responseType: 'blob',
      params: tipo ? { tipo } : undefined,
    });
    return res.data as Blob;
  },
  downloadTodosDocumentosZip: async (id: string): Promise<Blob> => {
    const res = await api.get(`/plano-trabalho/${id}/documentos/zip`, { responseType: 'blob' });
    return res.data as Blob;
  },
  downloadExtratoXlsx: async (id: string): Promise<Blob> => {
    const res = await api.get(`/plano-trabalho/${id}/extrato/xlsx`, { responseType: 'blob' });
    return res.data as Blob;
  },
  downloadExtratoPdf: async (id: string): Promise<Blob> => {
    const res = await api.get(`/plano-trabalho/${id}/extrato/pdf`, { responseType: 'blob' });
    return res.data as Blob;
  },
  gerarPdfParecer: async (id: string, tipo: string): Promise<PlanoTrabalhoParecerAnexo> => {
    const res = await api.post(`/plano-trabalho/${id}/parecer-anexo/gerar-pdf`, null, {
      params: { tipo },
    });
    return res.data as PlanoTrabalhoParecerAnexo;
  },
  getCronograma: async (id: string) => {
    const res = await api.get(`/plano-trabalho/${id}/cronograma`);
    return res.data as any[];
  },
  getPrevisao: async (id: string) => {
    const res = await api.get(`/plano-trabalho/${id}/previsao`);
    return res.data as { totalPlano: number; anos: Array<{ year: string; receitas: number; despesas: number; }> };
  },
  getAplicacao: async (id: string) => {
    const res = await api.get(`/plano-trabalho/${id}/aplicacao`);
    return res.data as Array<{ metaId: string; metaTitulo: string; items: any[]; totalMeta: number; orcamentos: any[] }>;
  },
  getDesembolso: async (id: string) => {
    const res = await api.get(`/plano-trabalho/${id}/desembolso`);
    return res.data as { meses: Array<any>; metasTotais: Array<any>; totalPlano: number };
  },
  downloadDesembolsoXlsx: async (id: string) => {
    const res = await api.get(`/plano-trabalho/${id}/desembolso/xlsx`, { responseType: 'blob' });
    return res.data as Blob;
  },
  vincularGestor: async (id: string, gestorId: number | null): Promise<PlanoTrabalho> => {
    const res = await api.put(`/plano-trabalho/${id}/gestor`, { gestorId });
    return res.data;
  },
  vincularProcuradorJuridico: async (id: string, procuradorJuridicoId: number | null): Promise<PlanoTrabalho> => {
    const res = await api.put(`/plano-trabalho/${id}/procurador-juridico`, { procuradorJuridicoId });
    return res.data;
  },
  reterParecerJuridico: async (id: string, motivo: string | null): Promise<PlanoTrabalho> => {
    const res = await api.put(`/plano-trabalho/${id}/retencao-juridica`, { motivo });
    return res.data;
  },
  liberarParecerJuridico: async (id: string): Promise<PlanoTrabalho> => {
    const res = await api.delete(`/plano-trabalho/${id}/retencao-juridica`);
    return res.data;
  },
  listarPlanosGestorJuridico: async (): Promise<PlanoTrabalho[]> => {
    const res = await api.get('/plano-trabalho/gestor-juridico/meus-planos');
    return res.data;
  },
  vincularComissao: async (id: string, comissaoId: string | null): Promise<PlanoTrabalho> => {
    const res = await api.put(`/plano-trabalho/${id}/comissao`, { comissaoId });
    return res.data;
  },
  listarPlanosDaMinhaComissao: async (): Promise<ComissaoMembroPainelPlano[]> => {
    const res = await api.get('/planos-de-trabalho/comissao/meus-planos');
    return res.data;
  },
  iniciarAnalise: async (id: string): Promise<void> => {
    await api.post(`/plano-trabalho/${id}/iniciar-analise`);
  },
  registrarAprovacaoPrefeito: async (id: string, referencia?: string): Promise<void> => {
    await api.post(`/plano-trabalho/${id}/aprovacao-prefeito`, { referencia: referencia ?? null });
  },
}

export default service;
